import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db, auth } from './firebase';
import Modal from './Modal';
import { getInitials, getAvatarStyle } from './utils';
import { sendCollaborationNotification } from './notificationService';

export default function CollaboratorsModal({ wishlistId, wishlist, currentUserId, onClose, viewOnly = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [currentCollaborators, setCurrentCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [wishlistData, setWishlistData] = useState(wishlist || null);

  // Determina la pagina corrente per passarla nelle navigazioni
  const currentPage = location.pathname;

  // Determine if current user is admin (owner of the wishlist)
  const isAdmin = wishlistData ? wishlistData.userId === currentUserId : false;
  const shouldShowSearchAndEdit = isAdmin && !viewOnly;

  useEffect(() => {
    const fetchData = async () => {
      if (wishlistId && !wishlist) {
        await loadWishlistData();
      } else if (wishlist) {
        setWishlistData(wishlist);
        await loadCollaboratorsData(wishlist);
      }
    };
    fetchData();
  }, [wishlistId, wishlist, currentUserId]);

  const loadWishlistData = async () => {
    try {
      const wishlistRef = doc(db, 'wishlists', wishlistId);
      const wishlistSnap = await getDoc(wishlistRef);
      if (wishlistSnap.exists()) {
        const data = { id: wishlistSnap.id, ...wishlistSnap.data() };
        setWishlistData(data);
        await loadCollaboratorsData(data);
      }
    } catch (error) {
      console.error('Errore nel caricamento wishlist:', error);
    }
  };

  const loadCollaboratorsData = async (wishlistToUse) => {
    try {
      const targetWishlist = wishlistToUse || wishlistData;

      if (!targetWishlist) {
        console.log('❌ Nessuna wishlist trovata per caricare i collaboratori');
        setCurrentCollaborators([]);
        return;
      }

      if (Array.isArray(targetWishlist.collaborators) && targetWishlist.collaborators.length > 0) {
        const collaboratorsPromises = targetWishlist.collaborators.map(async (collaboratorId) => {
          try {
            const userRef = doc(db, 'users', collaboratorId);
            const userSnap = await getDoc(userRef);
            return userSnap.exists() ? { id: userSnap.id, ...userSnap.data() } : null;
          } catch (error) {
            console.error('Errore caricamento collaboratore:', collaboratorId, error);
            return null;
          }
        });

        const collaboratorsData = await Promise.all(collaboratorsPromises);
        setCurrentCollaborators(collaboratorsData.filter(Boolean));
      } else {
        setCurrentCollaborators([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento collaboratori:', error);
      setCurrentCollaborators([]);
    }
  };

  const searchUsers = async (term) => {
    if (!term.trim() || !shouldShowSearchAndEdit) return;

    setLoading(true);
    try {
      // Cerca per displayName (case insensitive)
      const normalizedTerm = term.toLowerCase();
      
      // Prima prova con la query standard
      let usersQuery = query(
        collection(db, 'users'),
        where('displayName', '>=', term),
        where('displayName', '<=', term + '\uf8ff')
      );

      let querySnapshot = await getDocs(usersQuery);
      let users = querySnapshot.docs.map(userDoc => ({ id: userDoc.id, ...userDoc.data() }));

      // Se non trova risultati, prova una ricerca più ampia
      if (users.length === 0) {
        console.log('🔍 Ricerca standard fallita, provo ricerca alternativa...');
        
        // Carica tutti gli utenti e filtra lato client (per piccoli dataset)
        const allUsersQuery = query(collection(db, 'users'));
        const allUsersSnapshot = await getDocs(allUsersQuery);
        
        users = allUsersSnapshot.docs
          .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
          .filter(user => {
            const displayName = (user.displayName || '').toLowerCase();
            return displayName.includes(normalizedTerm);
          });
      }

      // Filtra utenti già collaboratori e utente corrente
      const filteredUsers = users.filter(user =>
        user.id !== currentUserId &&
        !(wishlistData?.collaborators || []).includes(user.id) &&
        user.displayName // Assicura che l'utente abbia un displayName
      );

      console.log('🔍 Risultati ricerca:', { term, foundUsers: filteredUsers.length });
      setSearchResults(filteredUsers);
    } catch (error) {
      console.error('❌ Errore nella ricerca utenti:', error);
      // In caso di errore, prova una ricerca di fallback
      try {
        console.log('🔄 Tentativo ricerca di fallback...');
        const fallbackQuery = query(collection(db, 'users'));
        const fallbackSnapshot = await getDocs(fallbackQuery);
        
        const fallbackUsers = fallbackSnapshot.docs
          .map(userDoc => ({ id: userDoc.id, ...userDoc.data() }))
          .filter(user => {
            const displayName = (user.displayName || '').toLowerCase();
            return displayName.includes(term.toLowerCase()) &&
                   user.id !== currentUserId &&
                   !(wishlistData?.collaborators || []).includes(user.id);
          });
        
        setSearchResults(fallbackUsers);
      } catch (fallbackError) {
        console.error('❌ Anche la ricerca di fallback è fallita:', fallbackError);
        setSearchResults([]);
      }
    }
    setLoading(false);
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    console.log('🔍 Ricerca utenti:', { value, length: value.length });
    
    if (value.length >= 2) {
      searchUsers(value);
    } else {
      setSearchResults([]);
      console.log('🔍 Ricerca troppo corta, pulisco risultati');
    }
  };

  const toggleUserSelection = (user) => {
    if (!shouldShowSearchAndEdit) return;
    setSelectedUsers(prev =>
      prev.find(u => u.id === user.id)
        ? prev.filter(u => u.id !== user.id)
        : [...prev, user]
    );
  };

  const addCollaborators = async () => {
    if (selectedUsers.length === 0 || !shouldShowSearchAndEdit || !wishlistData) return;

    try {
      const userIds = selectedUsers.map(user => user.id);
      const wishlistRef = doc(db, 'wishlists', wishlistData.id);

      await updateDoc(wishlistRef, {
        collaborators: arrayUnion(...userIds)
      });

      // Invia notifiche ai nuovi collaboratori
      const currentUserSnap = await getDoc(doc(db, 'users', currentUserId));
      const currentUserData = currentUserSnap.data();
      
      for (const user of selectedUsers) {
        await sendCollaborationNotification(
          currentUserId,
          currentUserData.displayName || 'Un utente',
          user.id,
          wishlistData.id
        );
      }

      // NON aggiornare lo stato locale - lascia che il reload automatico gestisca tutto
      await loadWishlistData(); // Ricarica i dati aggiornati da Firestore
      setSelectedUsers([]);
      setSearchTerm('');
      setSearchResults([]);
    } catch (error) {
      console.error('Errore nell\'aggiunta dei collaboratori:', error);
    }
  };

  const removeCollaborator = async (userId) => {
    if (!shouldShowSearchAndEdit || !wishlistData) return;
    try {
      const wishlistRef = doc(db, 'wishlists', wishlistData.id);
      await updateDoc(wishlistRef, {
        collaborators: arrayRemove(userId)
      });

      // NON aggiornare lo stato locale - lascia che il reload automatico gestisca tutto
      await loadWishlistData(); // Ricarica i dati aggiornati da Firestore
    } catch (error) {
      console.error('Errore nella rimozione del collaboratore:', error);
    }
  };

  const modalTitle = shouldShowSearchAndEdit ? t('wishlist.manageCollaborators') : t('wishlist.viewCollaborators');

    const navigateToUser = (userId) => {
      // Salva la posizione di scroll corrente prima di navigare
      sessionStorage.setItem('fromScroll', window.scrollY.toString());
      sessionStorage.setItem('fromPage', currentPage);
      navigate(`/utente/${userId}`, { state: { from: currentPage } });
    };

  return (
    <Modal
      title={modalTitle}
      onClose={onClose}
      showModal={true}
      customContent={true}
    >
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.modalTitle}>{modalTitle}</h3>
        </div>

        <div style={styles.container}>
          {shouldShowSearchAndEdit && (
            <>
              <div style={styles.section}>
                <h4 style={styles.sectionTitle}>{t('wishlist.addCollaborator')}</h4>
                <input
                  type="text"
                  placeholder={t('wishlist.searchUsers')}
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={styles.searchInput}
                />
                {loading && <p style={styles.loadingText}>{t('common.loading')}</p>}
                {searchResults.length > 0 && (
                  <div style={styles.searchResults}>
                    {searchResults.map(user => (
                      <div key={user.id} style={styles.userItem}>
                        <div style={styles.userInfo}>
                          {(user.photoURL || user.avatar)
                            ? (
                              <img
                                src={user.photoURL || user.avatar}
                                alt={user.displayName}
                                style={styles.avatar}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  const fallback = e.target.nextElementSibling;
                                  if (fallback) fallback.style.display = 'flex';
                                }}
                              />
                            ) : null}
                          {!(user.photoURL || user.avatar) && (
                            <div style={{ ...getAvatarStyle(32), marginRight: '12px' }}>
                              {getInitials(user.displayName || 'Utente')}
                            </div>
                          )}
                          <span
                            style={{
                              ...styles.userName,
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            onClick={() => {
                              onClose(); // Chiudi il modale prima di navigare
                              navigateToUser(user.id);
                            }}
                          >
                            {user.displayName || 'Utente'}
                          </span>
                        </div>
                        <button
                          onClick={() => toggleUserSelection(user)}
                          style={{
                            ...styles.selectButton,
                            backgroundColor: selectedUsers.find(u => u.id === user.id) ? '#000' : '#eee',
                            color: selectedUsers.find(u => u.id === user.id) ? '#fff' : '#000'
                          }}
                        >
                          {selectedUsers.find(u => u.id === user.id) ? t('common.remove') : t('common.add')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedUsers.length > 0 && (
                <div style={styles.section}>
                  <h4 style={styles.sectionTitle}>{t('wishlist.selectedUsers')}</h4>
                  <div style={styles.selectedUsers}>
                    {selectedUsers.map(user => (
                      <div key={user.id} style={styles.selectedUserItem}>
                        {(user.photoURL || user.avatar) ? (
                          <img
                            src={user.photoURL || user.avatar}
                            alt={user.displayName}
                            style={styles.smallAvatar}
                          />
                        ) : (
                          <div style={{ ...getAvatarStyle(24) }}>
                            {getInitials(user.displayName || 'Utente')}
                          </div>
                        )}
                        <span style={styles.selectedUserName}>{user.displayName || 'Utente'}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={addCollaborators} style={styles.addButton}>
                    {t('wishlist.addCollaborators')}
                  </button>
                </div>
              )}
            </>
          )}

          <div style={styles.section}>
            <h4 style={styles.sectionTitle}>{t('wishlist.currentCollaborators')}</h4>
            {currentCollaborators.length === 0 ? (
              <p style={styles.emptyText}>{t('wishlist.noCollaborators')}</p>
            ) : (
              <div style={styles.collaboratorsList}>
                {currentCollaborators.map(collaborator => (
                  <div key={collaborator.id} style={styles.collaboratorItem}>
                    <div style={styles.collaboratorInfo}>
                      {(collaborator.photoURL || collaborator.avatar) ? (
                        <>
                          <img
                            src={collaborator.photoURL || collaborator.avatar}
                            alt={collaborator.displayName}
                            style={styles.avatar}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallback = e.target.nextElementSibling;
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          <div style={{
                            ...getAvatarStyle(32),
                            ...styles.avatarFallback,
                            display: 'none'
                          }}>
                            {getInitials(collaborator.displayName || 'Utente')}
                          </div>
                        </>
                      ) : (
                        <div style={{ ...getAvatarStyle(32), ...styles.avatarFallback }}>
                          {getInitials(collaborator.displayName || 'Utente')}
                        </div>
                      )}
                      <span
                        style={{
                          ...styles.collaboratorName,
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                        onClick={() => {
                          onClose(); // Chiudi il modale prima di navigare
                          navigateToUser(collaborator.id);
                        }}
                      >
                        {collaborator.displayName || 'Utente'}
                      </span>
                    </div>
                    {shouldShowSearchAndEdit && (
                      <button
                        onClick={() => removeCollaborator(collaborator.id)}
                        style={styles.removeButton}
                      >
                        {t('common.remove')}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  modal: {
    background: "#fff",
    padding: "8px",
    borderRadius: "10px",
    width: "90%",
    maxWidth: "320px",
    minWidth: "220px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    textAlign: "left",
    maxHeight: "90vh",
    position: "relative",
    margin: "auto",
    overflowX: "hidden"
  },
  container: {
    fontFamily: 'Poppins, sans-serif',
    maxHeight: '82vh',
    overflowY: 'auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
    paddingBottom: '1px'
  },
  modalTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#000',
    margin: 0
  },
  closeButton: {
    background: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '50%',
    width: '26px',
    height: '26px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  viewOnlyText: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '16px',
    textAlign: 'center',
    fontStyle: 'italic'
  },
  section: {
    marginBottom: '8px'
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: '600',
    marginBottom: '4px',
    color: '#000',
    textAlign: 'left'
  },
  emptyText: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
    margin: '20px 0'
  },
  collaboratorsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  collaboratorItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px'
  },
  collaboratorInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    marginRight: '8px',
  },
  avatarFallback: {
    marginRight: '8px'
  },
  collaboratorName: {
    fontSize: '14px',
    fontWeight: '500',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  removeButton: {
    background: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 8px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500'
  },
  searchInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box',
    marginBottom: '6px',
    fontFamily: 'Poppins, sans-serif',
    outline: 'none',
    transition: 'border-color 0.2s ease',
    minWidth: 0
  },
  loadingText: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center'
  },
  searchResults: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    maxHeight: '120px',
    overflowY: 'auto'
  },
  userItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px',
    backgroundColor: '#f5f5f5',
    borderRadius: '6px'
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  userName: {
    fontSize: '14px',
    fontWeight: '500',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  selectButton: {
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '500'
  },
  selectedUsers: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '10px'
  },
  selectedUserItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: '#e8f5e8',
    borderRadius: '16px'
  },
  smallAvatar: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    objectFit: 'cover'
  },
  selectedUserName: {
    fontSize: '13px',
    fontWeight: '500'
  },
  addButton: {
    width: '100%',
    padding: '10px',
    backgroundColor: '#000',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  }
};